import AccessTimeIcon from '@mui/icons-material/AccessTime'
import BusinessIcon from '@mui/icons-material/Business'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import VideoChatIcon from '@mui/icons-material/VideoChat'
import { Collapse, IconButton } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { APP_CONSTANTS, currency_list } from 'src/consumer/constants'
import { prefetchBookingPage } from '../EventBooking/bookingPagePrefetchCache'
import {
  getDataWithoutParams,
  getPublicDataWithoutParams,
} from 'src/consumer/services/featureServices'
import { getImageLink, isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { LoadingSpinner } from '../Loaders'
import { PublicAppFooter } from '../PublicAppFooter'
import PublicAppHeader from '../PublicAppHeader'
import {
  EventListingBookButton,
  EventListingEventContainer,
  EventListingEventDescription,
  EventListingEventHeader,
  EventListingEventTitle,
  EventListingExpandContent,
  EventListingHero,
  EventListingMentorAvatar,
  EventListingMentorBio,
  EventListingMentorInfo,
  EventListingMentorName,
  EventListingMetaLabel,
  EventListingMetaRow,
  EventListingMetaValue,
  EventListingPageWrap,
  EventListingContent,
  EventListingPriceFinal,
  EventListingPriceRow,
  EventListingPriceStrikethrough,
  EventListingQuals,
  EventListingQualsLabel,
  EventListingQualsText,
  EventListingSection,
  EventListingSectionSubtitle,
  EventListingSectionTitle,
  EventListingSpecialTag,
  EventListingEventList,
  EventListingReferredBadge,
  EventListingReferredCard,
  EventListingReferredContact,
  EventListingReferredEmail,
  EventListingReferredHeading,
  EventListingReferredText,
} from './EventListingStyles'
import { LocalizationContext } from 'src/consumer/context/LocalizationContext'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'

export default function EventListing({ openedAsWebview, clinicId: clinicIdProp }) {
  const { mentorSlug } = useParams()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const clinicId =
    clinicIdProp ?? searchParams.get('clinicId') ?? location?.state?.clinicId ?? undefined
  const [plansData, setPlansData] = useState([])
  const [selectedEventId, setSelectedEventId] = useState()
  const [displayEventTypes, setDisplayEventTypes] = useState([])

  const [professionalDataLoading, setProfessionalDataLoading] = useState(true)
  const [professionalData, setProfessionalData] = useState(null)
  const [professionalDataError, setProfessionalDataError] = useState(null)

  async function pageSetup() {
    const response = await getDataWithoutParams('/api/subscription-plan/1on1')
    if (response?.status === 200 && response?.data?.length > 0) {
      setPlansData(response?.data)
    }
  }

  useEffect(() => {
    pageSetup()
  }, [])

  useEffect(() => {
    if (!mentorSlug || mentorSlug === 'undefined' || mentorSlug === 'null') return
    let cancelled = false
    ;(async () => {
      setProfessionalDataLoading(true)
      const clinicParam =
        clinicId != null && clinicId !== ''
          ? `&clinicId=${encodeURIComponent(clinicId)}`
          : ''
      const listingRes = await getPublicDataWithoutParams(
        `/api/public/mentor-events-listing?mentorSlug=${encodeURIComponent(mentorSlug)}${clinicParam}`,
      )
      if (cancelled) return
      const status = listingRes?.response?.status ?? listingRes?.status
      const body = listingRes?.response?.data ?? listingRes?.data
      const code = body?.code
      if (status !== 200 || !isCommonResponseSuccessful(code)) {
        setProfessionalDataError(listingRes)
        const profileRes = await getPublicDataWithoutParams(
          `/api/service-provider?slug=${encodeURIComponent(mentorSlug)}&include=qualifications,heroCardImage`,
        )
        if (cancelled) return
        const profileStatus = profileRes?.response?.status ?? profileRes?.status
        const profile = profileStatus === 200 ? profileRes?.data : null
        if (profile && typeof profile === 'object') {
          setProfessionalData(profile)
          setDisplayEventTypes([])
          setProfessionalDataError(null)
        } else {
          navigate('/404')
        }
        setProfessionalDataLoading(false)
        return
      }
      const data = body?.data
      const sessions = Array.isArray(data?.sessions) ? data.sessions : []
      const sortedSessions = sessions.slice().sort((a, b) => (a?.free ? -1 : b?.free ? 1 : 0))
      setDisplayEventTypes(sortedSessions)
      setSelectedEventId(sortedSessions?.[0]?.uid)

      const mentorId = data?.mentorId
      const slug = data?.slug ?? mentorSlug
      const profileRes = await getPublicDataWithoutParams(
        `/api/service-provider?id=${encodeURIComponent(mentorId || '')}&slug=${encodeURIComponent(slug || '')}&include=qualifications,heroCardImage`,
      )
      if (cancelled) return
      const profileStatus = profileRes?.response?.status ?? profileRes?.status
      const profile = profileStatus === 200 ? profileRes?.data : null
      if (profile && typeof profile === 'object') {
        setProfessionalData(profile)
      }
      setProfessionalDataLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [mentorSlug, clinicId])

  if (professionalDataLoading) {
    return <LoadingSpinner bgColor="#F5F5F599" dark />
  } // loader if secure setup data is coming

  const serviceProviderStatus = professionalDataError?.response?.status
  const listingFailed = professionalDataError && (serviceProviderStatus === 404 || serviceProviderStatus === 400)
  if (!professionalDataLoading && listingFailed && !professionalData) {
    navigate('/404')
    return null
  }

  if (!Boolean(mentorSlug) || mentorSlug === 'undefined' || mentorSlug === 'null') {
    return (
      <>
        <PublicAppHeader />
        <div>
          ⚠️ Invalid Request, Please try again or choose another professional. If you are still
          unable to book session, please write to us here
        </div>
        <div>📨{APP_CONSTANTS.SUPPORT_EMAIL}</div>
      </>
    )
  } // handling cases when url is not valid

  function goToLogin() {
    navigate('/login', {
      state: {
        redirectAfterLogin: true,
        redirectAfterRegistration: true,
        redirectAfterOnboarding: false,
        redirectTo: window.location.pathname,
        redirectData: {},
      },
    })
  }

  function goToRegister() {
    navigate('/onboarding', {
      state: {
        redirectAfterLogin: true,
        redirectAfterRegistration: true,
        redirectTo: window.location.pathname,
        redirectData: {},
      },
    })
  }

  return (
    <>
      <SEOComponent
        title={`Book a session with ${professionalData?.name} | AUMHUM`}
        description={professionalData?.shortDesc || professionalData?.longDesc}
        image={getImageLink(professionalData?.heroCardImage) || APP_CONSTANTS.APP_LOGO_LINK}
        keywordsArr={['mentor', 'online', 'job']}
      />
      {sessionStorage.getItem('isThisWebviewSession') || openedAsWebview ? (
        <div
          style={{
            padding: '10px 20px',
            position: 'fixed',
            top: 0,
            background: '#fff',
            width: '100%',
          }}
        >
          {sessionStorage.getItem('isThisWebviewSession') && (
            <IconButton onClick={() => window.history.back()}>
              <KeyboardBackspaceIcon sx={{ fontSize: 28, color: 'black', marginRight: '5px' }} />
            </IconButton>
          )}
        </div>
      ) : (
        <PublicAppHeader
          config={{ showLinks: false }}
          loginFunction={goToLogin}
          registerFunction={goToRegister}
        />
      )}
      <EventListingUI
        professionalData={professionalData}
        displayEventTypes={displayEventTypes}
        plansData={plansData}
        selectedEventId={selectedEventId}
        setSelectedEventId={setSelectedEventId}
      />
      {!(sessionStorage.getItem('isThisWebviewSession') || openedAsWebview) && <PublicAppFooter />}
    </>
  )
}

export function EventListingUI({
  professionalData,
  displayEventTypes,
  plansData,
  selectedEventId,
  setSelectedEventId,
}) {
  const { localizedData } = useContext(LocalizationContext)
  const myLiveSessionsStrings = localizedData?.strings?.myLiveSessionsPage

  // Preload EventBooking route chunk so "Book Now" navigation doesn't show a loader
  useEffect(() => {
    if (displayEventTypes?.length > 0) {
      import('../EventBooking')
    }
  }, [displayEventTypes?.length])

  // Prefetch booking page data when user expands an event so the booking page can show instantly
  useEffect(() => {
    if (professionalData?.slug && selectedEventId) {
      prefetchBookingPage(professionalData.slug, selectedEventId)
    }
  }, [professionalData?.slug, selectedEventId])

  const noPublicSessions = displayEventTypes?.length === 0 && professionalData

  return (
    <EventListingPageWrap>
      <EventListingContent>
        <EventListingHero>
          <EventListingMentorAvatar
            src={getImageLink(professionalData?.profilePicId)}
            alt=""
          />
          <EventListingMentorInfo>
            <EventListingMentorName>{professionalData?.name}</EventListingMentorName>
            {professionalData?.shortDesc && (
              <EventListingMentorBio>{professionalData.shortDesc}</EventListingMentorBio>
            )}
            {Array.isArray(professionalData?.personalize?.qualifications) &&
              professionalData.personalize.qualifications.length > 0 && (
                <EventListingQuals>
                  <EventListingQualsLabel>Qualifications</EventListingQualsLabel>
                  <EventListingQualsText>
                    {professionalData.personalize.qualifications.join(', ')}
                  </EventListingQualsText>
                </EventListingQuals>
              )}
          </EventListingMentorInfo>
        </EventListingHero>

        {noPublicSessions ? (
          <EventListingReferredCard>
            <EventListingReferredBadge>By referral</EventListingReferredBadge>
            <EventListingReferredHeading>
              {professionalData?.name} accepts sessions by referral only
            </EventListingReferredHeading>
            <EventListingReferredText>
              This practitioner focuses on referred and special cases. Sessions are not available for
              open booking. If you have been referred or have a special request, please reach out
              through your referring clinician or contact us below.
            </EventListingReferredText>
            <EventListingReferredContact>
              For referral or enquiries:{' '}
              <EventListingReferredEmail href={`mailto:${APP_CONSTANTS.SUPPORT_EMAIL}`}>
                {APP_CONSTANTS.SUPPORT_EMAIL}
              </EventListingReferredEmail>
            </EventListingReferredContact>
          </EventListingReferredCard>
        ) : (
        <EventListingSection>
          <EventListingSectionTitle>
            {`Sessions with ${professionalData?.name}`}
          </EventListingSectionTitle>
          <EventListingSectionSubtitle>
            {myLiveSessionsStrings?.choose_a_session_to_see_availability ||
              'Choose a session to see availability'}
          </EventListingSectionSubtitle>

          <EventListingEventList>
            {displayEventTypes?.map((event, index) => {
              let currencySymbol, price, finalPrice, planForThisEvent
              if (!event?.free) {
                planForThisEvent = plansData?.find((item) => item?.id === event?.subscriptionPlanId)
                currencySymbol = currency_list?.find(
                  (item) => item?.code === planForThisEvent?.currency,
                )?.symbol
                price = planForThisEvent?.price
                finalPrice = planForThisEvent?.finalPrice
              }
              const isSelected = event?.uid === selectedEventId

              return (
                <EventListingEventContainer
                  key={event?.uid ?? index}
                  $selected={isSelected}
                  onClick={() => setSelectedEventId(event?.uid)}
                >
                  <EventListingEventHeader>
                    <EventListingEventTitle>{event?.title}</EventListingEventTitle>
                    {event?.free && (
                      <EventListingSpecialTag>
                        <svg
                          stroke="currentColor"
                          fill="currentColor"
                          strokeWidth="0"
                          viewBox="0 0 576 512"
                          height="0.9em"
                          width="0.9em"
                          aria-hidden
                        >
                          <path d="M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z" />
                        </svg>
                        {myLiveSessionsStrings?.free || 'Free'}
                      </EventListingSpecialTag>
                    )}
                  </EventListingEventHeader>
                  {event?.description && (
                    <EventListingEventDescription>{event.description}</EventListingEventDescription>
                  )}

                  <Collapse in={isSelected} unmountOnExit>
                    <EventListingExpandContent>
                      <EventListingMetaRow>
                        <EventListingMetaLabel>
                          {myLiveSessionsStrings?.duration || 'Duration'}:
                        </EventListingMetaLabel>
                        <EventListingMetaValue>
                          {event?.durationInMins} mins
                          <AccessTimeIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                        </EventListingMetaValue>
                      </EventListingMetaRow>
                      <EventListingMetaRow>
                        <EventListingMetaLabel>
                          {myLiveSessionsStrings?.price || 'Price'}:
                        </EventListingMetaLabel>
                        <EventListingMetaValue>
                          {planForThisEvent && currencySymbol && price != null && finalPrice != null ? (
                            <EventListingPriceRow>
                              <EventListingPriceStrikethrough>
                                {currencySymbol}
                                {price}
                              </EventListingPriceStrikethrough>
                              <EventListingPriceFinal>
                                {currencySymbol}
                                {finalPrice}
                              </EventListingPriceFinal>
                            </EventListingPriceRow>
                          ) : (
                            'Free'
                          )}
                        </EventListingMetaValue>
                      </EventListingMetaRow>
                      <EventListingMetaRow>
                        <EventListingMetaLabel>
                          {myLiveSessionsStrings?.mode || 'Mode'}:
                        </EventListingMetaLabel>
                        <EventListingMetaValue>
                          {event?.meetingSrc === 'offline' ? (
                            <>
                              <BusinessIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                              In Person
                            </>
                          ) : (
                            <>
                              <VideoChatIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                              Video Call
                            </>
                          )}
                        </EventListingMetaValue>
                      </EventListingMetaRow>
                      {event?.meetingSrc === 'offline' && event?.location && (
                        <EventListingMetaRow>
                          <EventListingMetaLabel>Location:</EventListingMetaLabel>
                          <EventListingMetaValue>
                            <LocationOnIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                            {[
                              event?.location?.address,
                              event?.location?.city,
                              event?.location?.state,
                              event?.location?.pincode,
                              event?.location?.country,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </EventListingMetaValue>
                        </EventListingMetaRow>
                      )}
                      <EventListingBookButton
                        as={Link}
                        to={`/mentor/${professionalData?.slug}/events/${event?.uid}`}
                      >
                        {myLiveSessionsStrings?.book_now || 'Book Now'}
                      </EventListingBookButton>
                    </EventListingExpandContent>
                  </Collapse>
                </EventListingEventContainer>
              )
            })}
          </EventListingEventList>
        </EventListingSection>
        )}
      </EventListingContent>
    </EventListingPageWrap>
  )
}
